import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Grid, List, Package, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import ProductFilters from '../../components/ProductFilters';
import EnhancedProductCard from '../../components/LargeFormat/EnhancedProductCard';
import CategoryHero from '../../components/LargeFormat/CategoryHero';
import CategorySidebar from '../../components/LargeFormat/CategorySidebar';
import { unifiedAPI } from '../../utils/api';
import { useStableSearchParams } from '../../hooks/useStableSearchParams';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // If already a full URL (http/https), return as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // If local path, prefix with API_URL
  if (imageUrl.startsWith('/')) {
    // Ensure /uploads paths use /api prefix for proper routing
    if (imageUrl.startsWith('/uploads/') && !imageUrl.startsWith('/api/')) {
      return `${API_URL}/api${imageUrl}`;
    }
    return `${API_URL}${imageUrl}`;
  }
  return imageUrl;
};

const LargeFormatProducts = () => {
  const { categoryId } = useParams();
  const { initialParams, setParams } = useStableSearchParams();
  
  // State
  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  
  // Read initial state from URL params (only on mount)
  const [filters, setFilters] = useState(() => ({
    categories: categoryId ? [categoryId] : (initialParams.categories?.split(',').filter(Boolean) || []),
    sort: initialParams.sort || 'name_asc',
    minPrice: initialParams.min_price ? parseFloat(initialParams.min_price) : null,
    maxPrice: initialParams.max_price ? parseFloat(initialParams.max_price) : null,
    status: initialParams.status || null,
    page: parseInt(initialParams.page || '1'),
    search: initialParams.search || '',
  }));

  const ITEMS_PER_PAGE = 24;
  
  // Track if first render to skip initial URL update
  const isFirstRender = useRef(true);

  // Fetch categories for sidebar using unified API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await unifiedAPI.getCategories({ product_type: 'large_format', include_counts: true });
        setAllCategories(response.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Update filters when categoryId param changes
  useEffect(() => {
    if (categoryId) {
      setFilters(prev => ({ ...prev, categories: [categoryId], page: 1 }));
    }
  }, [categoryId]);

  // Update URL when filters change (using stable hook)
  useEffect(() => {
    // Skip first render - we just read from URL
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    const params = {};
    // Don't put categories in URL if we're on a category-specific route
    if (!categoryId && filters.categories?.length) {
      params.categories = filters.categories.join(',');
    }
    if (filters.sort && filters.sort !== 'name_asc') params.sort = filters.sort;
    if (filters.minPrice !== null) params.min_price = filters.minPrice;
    if (filters.maxPrice !== null) params.max_price = filters.maxPrice;
    if (filters.status) params.status = filters.status;
    if (filters.page > 1) params.page = filters.page;
    if (filters.search) params.search = filters.search;
    
    setParams(params);
  }, [filters, categoryId, setParams]);

  // Fetch products using unified API
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await unifiedAPI.getProducts({
        product_type: 'large_format',
        page: filters.page,
        limit: ITEMS_PER_PAGE,
        sort: filters.sort,
        category_id: filters.categories[0] || undefined,
        search: filters.search || undefined,
      });
      
      setProducts(response.products || []);
      setTotalProducts(response.total || 0);
      if (response.filter_options) {
        setFilterOptions(response.filter_options);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch products when filters change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Filter handlers
  const handleSortChange = (sort) => {
    setFilters(prev => ({ ...prev, sort, page: 1 }));
  };

  const handleCategoryChange = (categories) => {
    setFilters(prev => ({ ...prev, categories, page: 1 }));
  };

  const handlePriceChange = (minPrice, maxPrice) => {
    setFilters(prev => ({ ...prev, minPrice, maxPrice, page: 1 }));
  };

  const handleStatusChange = (status) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      categories: categoryId ? [categoryId] : [],
      sort: 'name_asc',
      minPrice: null,
      maxPrice: null,
      status: null,
      page: 1,
      search: '',
    });
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryClick = (catId) => {
    if (filters.categories.includes(catId)) {
      // Deselect
      setFilters(prev => ({ ...prev, categories: prev.categories.filter(c => c !== catId), page: 1 }));
    } else {
      // Select (replace for single-select behavior, or add for multi-select)
      setFilters(prev => ({ ...prev, categories: [catId], page: 1 }));
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);

  // Get current category name for header
  const currentCategory = allCategories.find(c => filters.categories.includes(c.id));

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Category Hero Banner */}
      <CategoryHero 
        category={currentCategory}
        categoryCode={currentCategory?.code}
        title={currentCategory ? currentCategory.name : 'All Products'}
        description={currentCategory ? currentCategory.description : 'Browse our complete catalog of large format printing products'}
        productCount={totalProducts}
        showPromo={true}
      />

      {/* Content with Sidebar */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Categories */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <CategorySidebar
              categories={allCategories}
              selectedCategories={filters.categories}
              onCategoryClick={handleCategoryClick}
              onAllProductsClick={() => setFilters(prev => ({ ...prev, categories: [], page: 1 }))}
              totalProductCount={allCategories.reduce((sum, c) => sum + (c.product_count || c.count || 0), 0)}
              loading={categoriesLoading}
            />
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Filters Bar (Sort, Price Range) */}
            <ProductFilters
              filterOptions={filterOptions}
              selectedCategories={[]} // Don't show category filter in toolbar since we have sidebar
              selectedSort={filters.sort}
              minPrice={filters.minPrice}
              maxPrice={filters.maxPrice}
              selectedStatus={filters.status}
              onCategoryChange={handleCategoryChange}
              onSortChange={handleSortChange}
              onPriceChange={handlePriceChange}
              onStatusChange={handleStatusChange}
              onClearFilters={handleClearFilters}
              loading={loading}
              hideCategoryFilter={true}
            />

            {/* Results Summary & View Toggle */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-ivory-600 font-body">
                {loading ? 'Loading...' : `${totalProducts} product${totalProducts !== 1 ? 's' : ''} found`}
              </p>
              <div className="flex items-center gap-4">
                {/* Search Input */}
                <div className="relative hidden sm:block">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory-400" />
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                    className="pl-9 w-64 bg-white border-ivory-300 text-ivory-800 placeholder:text-ivory-400"
                    data-testid="product-search-input"
                  />
                </div>
                {/* View Toggle */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-gold-500 text-white' : 'bg-ivory-200 text-ivory-600 hover:bg-ivory-300'}`}
                    data-testid="view-grid"
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-gold-500 text-white' : 'bg-ivory-200 text-ivory-600 hover:bg-ivory-300'}`}
                    data-testid="view-list"
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
              </div>
            )}

            {/* Empty State */}
            {!loading && products.length === 0 && (
              <Card className="bg-ivory-100 border border-ivory-300/50">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-ivory-300 mx-auto mb-4" />
                  <h3 className="text-xl font-heading font-semibold text-ivory-800 mb-2">
                    No products found
                  </h3>
                  <p className="text-ivory-500 font-body mb-6">
                    Try selecting a different category or adjusting your filters
                  </p>
                  <Button onClick={handleClearFilters} className="btn-luxury-outline">
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Products Grid */}
            {!loading && products.length > 0 && (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'
                : 'space-y-4'
              }>
                {products.map((product) => (
                  <EnhancedProductCard 
                    key={product.id} 
                    product={product} 
                    viewMode={viewMode}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 1 || loading}
                  className="btn-luxury-outline"
                  data-testid="prev-page"
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (filters.page <= 3) {
                      pageNum = i + 1;
                    } else if (filters.page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = filters.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg font-body ${
                          pageNum === filters.page
                            ? 'bg-gold-500 text-white'
                            : 'bg-ivory-200 text-ivory-600 hover:bg-ivory-300'
                        }`}
                        data-testid={`page-${pageNum}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page === totalPages || loading}
                  className="btn-luxury-outline"
                  data-testid="next-page"
                >
                  Next
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default LargeFormatProducts;
